import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import QueryKeys from '@constants/queryKeys';
import { markOtherNotificationRead } from '@worker/services/inbox/api';
import type { OtherNotificationResponse } from '@th-types/worker-notifications.type';

type MutationContext = {
  previousQueries: Array<
    [readonly unknown[], OtherNotificationResponse | undefined]
  >;
};

export function useMarkOtherNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) =>
      markOtherNotificationRead(notificationId),
    onMutate: async (notificationId: number): Promise<MutationContext> => {
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.WorkerInbox.OTHER_NOTIFICATIONS],
      });

      const previousQueries =
        queryClient.getQueriesData<OtherNotificationResponse>({
          queryKey: [QueryKeys.WorkerInbox.OTHER_NOTIFICATIONS],
        });

      // The notification lives on a single page, but `countNew` is duplicated
      // across every cached page. Decide once whether the item was unread,
      // then apply the same decrement to all pages — otherwise the count goes
      // stale on pages that don't contain the target item.
      const wasUnread = previousQueries.some(([, data]) =>
        data?.list.some(
          (notification) =>
            notification.id === notificationId && !notification.isRead
        )
      );

      queryClient.setQueriesData<OtherNotificationResponse>(
        { queryKey: [QueryKeys.WorkerInbox.OTHER_NOTIFICATIONS] },
        (previousData) => {
          if (!previousData) return previousData;
          return {
            ...previousData,
            countNew: wasUnread
              ? Math.max(0, previousData.countNew - 1)
              : previousData.countNew,
            list: previousData.list.map((notification) =>
              notification.id === notificationId
                ? { ...notification, isRead: true }
                : notification
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _id, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      notifications.show({
        color: 'red',
        title: 'Could not mark as read',
        message: 'Please try again.',
      });
    },
  });
}
